<script setup>
import { computed } from 'vue';
import BaseBubble from './Base.vue';
import { useI18n } from 'vue-i18n';
import { CONTENT_TYPES } from '../constants.js';
import { useMessageContext } from '../provider.js';
import { useInbox } from 'dashboard/composables/useInbox';

const { content, contentAttributes, contentType } = useMessageContext();
const { t } = useI18n();
const { isAWebWidgetInbox } = useInbox();

const isEmail = value => {
  if (!value) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim());
};

const formValues = computed(() => {
  if (contentType.value === CONTENT_TYPES.FORM) {
    const { items, submittedValues = [] } = contentAttributes.value;

    if (submittedValues.length) {
      return submittedValues.map(submittedValue => {
        const item = items.find(
          formItem => formItem.name === submittedValue.name
        );
        return {
          title: submittedValue.value,
          value: submittedValue.value,
          label: item?.label,
        };
      });
    }

    return [];
  }

  if (contentType.value === CONTENT_TYPES.INPUT_SELECT) {
    const [item] = contentAttributes.value?.submittedValues ?? [];
    if (!item) return [];

    return [
      {
        title: item.title,
        value: item.value,
        label: '',
      },
    ];
  }

  return [];
});
</script>

<template>
  <BaseBubble
    class="wlab-form-bubble px-5 py-5"
    data-bubble-name="form"
    data-testid="chat-survey-panel"
  >
    <p class="wlab-form-bubble__title">
      <span v-dompurify-html="content || t('CONTACT.PRE_CHAT')" />
    </p>

    <div class="privacy-masker">
      <div v-if="formValues.length" class="wlab-form-bubble__rows">
        <div
          v-for="(item, idx) in formValues"
          :key="`${item.title}-${idx}`"
          class="wlab-form-bubble__row"
        >
          <p class="wlab-form-bubble__question">
            {{ (item.label || t('CONVERSATION.RESPONSE')) + ':' }}
          </p>
          <div class="wlab-form-bubble__answer">
            <a
              v-if="isEmail(item.title)"
              :href="`mailto:${item.title}`"
              target="_blank"
              rel="noopener noreferrer"
              tabindex="0"
            >
              <span>{{ item.title }}</span>
            </a>
            <span v-else>{{ item.title }}</span>
          </div>
        </div>
      </div>
      <div v-else-if="isAWebWidgetInbox" class="wlab-form-bubble__empty">
        {{ t('CONVERSATION.NO_RESPONSE') }}
      </div>
    </div>
  </BaseBubble>
</template>

<style lang="scss">
/* Pre-chat / form bubble (data-testid="chat-survey-panel") in PURE dark theme.
   The global .wlab-form-bubble* text colors are hardcoded dark (#131317 / #62626d
   with !important) which is unreadable on the dark bubble surface. Mix theme is
   excluded because there the bubble is rendered light (see _woot.scss
   `.message-bubble-container .left-bubble`), so its dark text is already fine. */
body.dark:not(.theme-mix) .wlab-form-bubble {
  background-color: #26272b !important;
  color: #e6edf3 !important;
}
body.dark:not(.theme-mix) .wlab-form-bubble__title,
body.dark:not(.theme-mix) .wlab-form-bubble__answer {
  color: #e6edf3 !important;
}
body.dark:not(.theme-mix) .wlab-form-bubble__question {
  color: rgba(230, 237, 243, 0.62) !important;
}
body.dark:not(.theme-mix) .wlab-form-bubble__answer a,
body.dark:not(.theme-mix) .wlab-form-bubble__answer a span {
  color: #7aa7ff !important;
}
</style>
